/**
 * Real-time Notification Client
 * Handles WebSocket connections and real-time notifications on the frontend
 */

import { io } from 'socket.io-client';

console.log('[DEBUG] notificationClient module loaded');

class NotificationClient {
    constructor() {
        this.socket = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.eventListeners = new Map();
        this.notificationCallbacks = [];
    }

    /**
     * Initialize connection to WebSocket server
     */
    connect(token) {
        if (this.socket && this.isConnected) {
            console.log('[NOTIFICATION_CLIENT] Already connected');
            return;
        }

        const serverUrl = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

        console.log('[NOTIFICATION_CLIENT] Connecting to:', serverUrl);
        console.log('[NOTIFICATION_CLIENT] Token length:', token ? token.length : 'No token');
        console.log('[NOTIFICATION_CLIENT] Environment:', {
            REACT_APP_SERVER_URL: process.env.REACT_APP_SERVER_URL,
            REACT_APP_SOCKET_IO_ENABLED: process.env.REACT_APP_SOCKET_IO_ENABLED
        });

        this.socket = io(serverUrl, {
            auth: {
                token: token
            },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: this.maxReconnectAttempts,
            reconnectionDelay: 1000,
            timeout: 10000
        });

        this.setupEventHandlers();
    }

    /**
     * Setup Socket.IO event handlers
     */
    setupEventHandlers() {
        // Connection events
        this.socket.on('connect', () => {
            this.isConnected = true;
            this.reconnectAttempts = 0;
            console.log('[NOTIFICATION_CLIENT] ✅ Connected to real-time server');
            console.log('[NOTIFICATION_CLIENT] Socket ID:', this.socket.id);
        });

        this.socket.on('connection-ack', (data) => {
            console.log('[NOTIFICATION_CLIENT] 🎯 Connection acknowledged:', data);
            this.emit('connection-established', data);
        });

        this.socket.on('disconnect', (reason) => {
            this.isConnected = false;
            console.log('[NOTIFICATION_CLIENT] ❌ Disconnected:', reason);
            this.emit('disconnected', { reason });
        });

        this.socket.on('connect_error', (error) => {
            console.error('[NOTIFICATION_CLIENT] ❌ Connection error:', error.message);
            this.emit('connection-error', { error: error.message });
        });

        this.socket.on('reconnect', (attemptNumber) => {
            console.log('[NOTIFICATION_CLIENT] 🔄 Reconnected after', attemptNumber, 'attempts');
            this.emit('reconnected', { attemptNumber });
        });

        this.socket.on('reconnect_failed', () => {
            console.error('[NOTIFICATION_CLIENT] ❌ Failed to reconnect after', this.maxReconnectAttempts, 'attempts');
            this.emit('reconnect-failed');
        });

        // Notification events
        this.socket.on('new-notification', (notification) => {
            console.log('[NOTIFICATION_CLIENT] 🔔 New notification:', notification);
            this.handleNotification(notification);
            this.emit('new-notification', notification);
        });

        this.socket.on('task-assigned', (data) => {
            console.log('[NOTIFICATION_CLIENT] 👷 Task assigned:', data);
            this.emit('task-assigned', data);
        });

        this.socket.on('status-update', (data) => {
            console.log('[NOTIFICATION_CLIENT] 📊 Status update:', data);
            this.emit('status-update', data);
        });

        this.socket.on('request-completed', (data) => {
            console.log('[NOTIFICATION_CLIENT] ✅ Request completed:', data);
            this.emit('request-completed', data);
        });

        this.socket.on('inventory-alert', (data) => {
            console.log('[NOTIFICATION_CLIENT] 📦 Inventory alert:', data);
            this.emit('inventory-alert', data);
        });

        // Real-time updates
        this.socket.on('user-connected', (data) => {
            console.log('[NOTIFICATION_CLIENT] 👋 User connected:', data.userName);
            this.emit('user-connected', data);
        });

        this.socket.on('user-disconnected', (data) => {
            console.log('[NOTIFICATION_CLIENT] 👋 User disconnected:', data.userName);
            this.emit('user-disconnected', data);
        });

        this.socket.on('notifications-update', (notifications) => {
            console.log('[NOTIFICATION_CLIENT] 📋 Notifications update:', notifications.length, 'notifications');
            this.emit('notifications-update', notifications);
        });

        // Health check
        this.socket.on('pong', () => {
            console.log('[NOTIFICATION_CLIENT] 🏓 Pong received');
        });

        // Error handling
        this.socket.on('error', (error) => {
            console.error('[NOTIFICATION_CLIENT] ❌ Server error:', error);
            this.emit('error', error);
        });
    }

    /**
     * Handle incoming notifications
     */
    handleNotification(notification) {
        // Send acknowledgment
        this.socket.emit('notification-received', {
            notificationId: notification.id,
            receivedAt: new Date().toISOString()
        });

        // Trigger browser notification if permissions granted
        this.showBrowserNotification(notification);

        // Call registered notification callbacks
        this.notificationCallbacks.forEach(callback => {
            try {
                callback(notification);
            } catch (error) {
                console.error('[NOTIFICATION_CLIENT] Error in notification callback:', error);
            }
        });
    }

    /**
     * Show browser notification
     */
    showBrowserNotification(notification) {
        if ('Notification' in window && Notification.permission === 'granted') {
            const options = {
                body: notification.message,
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                tag: notification.type,
                data: notification.data,
                requireInteraction: notification.type === 'urgent'
            };

            const browserNotification = new Notification(notification.title, options);

            browserNotification.onclick = () => {
                window.focus();
                browserNotification.close();

                // Emit click event for handling in components
                this.emit('notification-clicked', notification);
            };

            // Auto-close after 5 seconds unless it requires interaction
            if (!options.requireInteraction) {
                setTimeout(() => {
                    browserNotification.close();
                }, 5000);
            }
        }
    }

    /**
     * Request latest notifications
     */
    requestNotifications() {
        if (this.isConnected) {
            this.socket.emit('request-notifications');
        }
    }

    /**
     * Send typing indicator
     */
    startTyping() {
        if (this.isConnected) {
            this.socket.emit('typing-start');
        }
    }

    stopTyping() {
        if (this.isConnected) {
            this.socket.emit('typing-stop');
        }
    }

    /**
     * Update user status
     */
    updateStatus(status) {
        if (this.isConnected) {
            this.socket.emit('user-status', status);
        }
    }

    /**
     * Send ping for connection health
     */
    ping() {
        if (this.isConnected) {
            this.socket.emit('ping');
        }
    }

    /**
     * Add event listener
     */
    on(event, callback) {
        if (!this.eventListeners.has(event)) {
            this.eventListeners.set(event, []);
        }
        this.eventListeners.get(event).push(callback);
    }

    /**
     * Remove event listener
     */
    off(event, callback) {
        if (this.eventListeners.has(event)) {
            const listeners = this.eventListeners.get(event);
            const index = listeners.indexOf(callback);
            if (index > -1) {
                listeners.splice(index, 1);
            }
        }
    }

    /**
     * Emit event to registered listeners
     */
    emit(event, data) {
        if (this.eventListeners.has(event)) {
            this.eventListeners.get(event).forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error('[NOTIFICATION_CLIENT] Error in event listener:', error);
                }
            });
        }
    }

    /**
     * Add notification callback
     */
    onNotification(callback) {
        this.notificationCallbacks.push(callback);
    }

    /**
     * Remove notification callback
     */
    offNotification(callback) {
        const index = this.notificationCallbacks.indexOf(callback);
        if (index > -1) {
            this.notificationCallbacks.splice(index, 1);
        }
    }

    /**
     * Disconnect from server
     */
    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
            this.isConnected = false;
            console.log('[NOTIFICATION_CLIENT] 👋 Disconnected from real-time server');
        }
    }

    /**
     * Get connection status
     */
    getConnectionStatus() {
        return {
            connected: this.isConnected,
            socketId: this.socket?.id || null,
            reconnectAttempts: this.reconnectAttempts
        };
    }
}

// Create singleton instance
const notificationClient = new NotificationClient();

export default notificationClient;
